package dairy.mynotes.login

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dairy.mynotes.repository.AuthRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * UI State for Login Screen
 */
data class LoginUiState(
    val isLoading: Boolean = false,
    val isLoggedIn: Boolean = false,
    val error: String? = null,
    val email: String = "",
    val password: String = "",
    // Forgot password
    val forgotPasswordEmail: String = "",
    val forgotPasswordLoading: Boolean = false,
    val forgotPasswordSuccess: Boolean = false,
    val forgotPasswordError: String? = null
)

/**
 * ViewModel for Login Screen
 * Handles authentication logic using AuthRepository
 */
class LoginViewModel : ViewModel() {
    private val authRepository = AuthRepository()

    private val _uiState = MutableStateFlow(LoginUiState())
    val uiState: StateFlow<LoginUiState> = _uiState.asStateFlow()

    /**
     * Update email in UI state
     */
    fun onEmailChange(email: String) {
        _uiState.value = _uiState.value.copy(email = email, error = null)
    }

    /**
     * Update password in UI state
     */
    fun onPasswordChange(password: String) {
        _uiState.value = _uiState.value.copy(password = password, error = null)
    }

    /**
     * Login with email and password
     */
    fun login() {
        val email = _uiState.value.email
        val password = _uiState.value.password

        // Validation
        if (email.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Email is required")
            return
        }
        if (password.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Password is required")
            return
        }

        // Start login
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true, error = null)

            val result = authRepository.login(email, password)
            if (result.isSuccess) {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoggedIn = true,
                    error = null
                )
            } else {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    error = result.exceptionOrNull()?.message ?: "Login failed"
                )
            }
        }
    }

    /**
     * Clear error message
     */
    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    /**
     * Update forgot password email field
     */
    fun onForgotPasswordEmailChange(email: String) {
        _uiState.value = _uiState.value.copy(
            forgotPasswordEmail = email,
            forgotPasswordError = null
        )
    }

    /**
     * Send password reset email via Firebase Auth
     */
    fun sendPasswordReset() {
        val email = _uiState.value.forgotPasswordEmail.trim()
        if (email.isBlank()) {
            _uiState.value = _uiState.value.copy(forgotPasswordError = "Please enter your email address")
            return
        }
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(forgotPasswordLoading = true, forgotPasswordError = null)
            authRepository.sendPasswordResetEmail(email).onSuccess {
                _uiState.value = _uiState.value.copy(
                    forgotPasswordLoading = false,
                    forgotPasswordSuccess = true
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(
                    forgotPasswordLoading = false,
                    forgotPasswordError = e.message ?: "Failed to send reset email"
                )
            }
        }
    }

    /**
     * Reset forgot password dialog state (call when dialog is dismissed)
     */
    fun resetForgotPasswordState() {
        _uiState.value = _uiState.value.copy(
            forgotPasswordEmail = "",
            forgotPasswordLoading = false,
            forgotPasswordSuccess = false,
            forgotPasswordError = null
        )
    }

    /**
     * Check if user is already logged in
     */
    fun checkLoginStatus() {
        val isLoggedIn = authRepository.isLoggedIn()
        _uiState.value = _uiState.value.copy(isLoggedIn = isLoggedIn)
    }
}
